import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import { State } from 'country-state-city';
import useRequest from '../../../components/hooks/use-req';
import useApi from '../../../components/hooks/req';
import { showToast } from '../../../components/toast';
import Input from '../../../components/input';
import Select from '../../../components/select';
import Button from '../../../components/button';
import { BVNIcon, GreenThick, MailIcon, WhatsappIcon } from '../../../assets/icon';
import { DateOfBirthPicker } from '../../../components/date-of-birth-picker';

interface ProfileFormData {
    first_name: string;
    last_name: string;
    bvn: string;
    phone: string;
    gender: string;
    date_of_birth: string;
    email: string;
    state: string;
}

interface EditProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdated?: () => void;
}

const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
];

const stateOptions = (State.getStatesOfCountry('NG') || []).map((state) => ({
    value: state.isoCode,
    label: state.name,
}));

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, onUpdated }) => {
    const authToken = localStorage.getItem('authToken');
    const [isVerified, setIsVerified] = useState(false);
    const [profileImage, setProfileImage] = useState('');
    const [previewImage, setPreviewImage] = useState('');
    const [uploading, setUploading] = useState(false);

    const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<ProfileFormData>({
        defaultValues: {
            first_name: '',
            last_name: '',
            bvn: '',
            phone: '',
            gender: '',
            date_of_birth: '',
            email: '',
            state: '',
        },
    });

    const { makeRequest: fetchProfile } = useRequest('/auth/auth/me', 'GET', {
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
    });

    const { makeRequest: updateProfile } = useRequest('/account/recommender/profile', 'PUT', {
        Authorization: `Bearer ${authToken}`,
    });

    const { makeRequest: uploadFile } = useApi('/account/profile-picture', 'POST', {
        Authorization: authToken ? `Bearer ${authToken}` : '',
    });

    useEffect(() => {
        if (!isOpen) return;

        const loadProfile = async () => {
            const [response] = await fetchProfile();
            const data = response?.data;
            if (!data) return;

            setIsVerified(!!data.is_verified);
            setProfileImage(data.image_url || '');
            setPreviewImage(data.image_url || '');

            reset({
                first_name: data.first_name || '',
                last_name: data.last_name || '',
                bvn: data.bvn || '',
                phone: data.phone || '',
                gender: data.gender || '',
                date_of_birth: data.date_of_birth || '',
                email: data.email || '',
                state: data.state || '',
            });
        };

        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setPreviewImage(URL.createObjectURL(file));

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        const [response] = await uploadFile(formData);
        setUploading(false);

        const uploadedUrl =
            response?.image_url ||
            response?.data?.url ||
            response?.file_url ||
            response?.document_url;

        if (uploadedUrl) {
            setProfileImage(uploadedUrl);
            showToast('Image uploaded successfully', true);
        } else {
            showToast(response?.message || 'Failed to upload image', false);
        }
    };

    const handleRemoveImage = () => {
        setProfileImage('');
        setPreviewImage('');
    };

    const handleSave = handleSubmit(async (formData) => {
        const [response] = await updateProfile({
            first_name: formData.first_name,
            last_name: formData.last_name,
            bvn: formData.bvn,
            phone: formData.phone,
            gender: formData.gender,
            date_of_birth: formData.date_of_birth,
            email: formData.email,
            state: formData.state,
            profile_image: profileImage,
        });

        if (response?.status) {
            showToast(response?.message || 'Profile updated successfully', true);
            onUpdated?.();
            onClose();
        } else {
            showToast(response?.message || 'Failed to update profile', false);
        }
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-6">
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                    aria-label="Close"
                    type="button"
                >
                    <X size={18} />
                </button>

                <h2 className="text-[22px] font-bold text-gray-900 mb-1">Edit Personal Information</h2>
                <p className="text-gray-500 font-medium mb-6">
                    Keep your profile updated. Edit Your VouchLinks personal information.
                </p>

                <div className="flex items-center gap-4 mb-1">
                    <div className="w-16 h-16 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                        {previewImage ? (
                            <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-gray-400 text-xs">No image</span>
                        )}
                    </div>

                    <label className="inline-flex items-center justify-center h-[44px] px-4 rounded-full bg-[#121212] text-white text-sm font-semibold cursor-pointer hover:opacity-75">
                        {uploading ? 'Uploading...' : 'Update Image'}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif"
                            className="hidden"
                            onChange={handleImageChange}
                            disabled={uploading}
                        />
                    </label>

                    {previewImage && (
                        <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="inline-flex items-center justify-center h-[44px] px-4 rounded-full border border-[#121212] text-[#333D4D] text-sm font-semibold hover:bg-gray-50"
                        >
                            Remove Image
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 mb-6">We support PNGs, JPEGs and GIFs under 2MB</p>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="first_name"
                            control={control}
                            rules={{ required: 'First name is required' }}
                            render={({ field, fieldState }) => (
                                <Input
                                    label="First Name"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState?.error?.message}
                                    className="w-full"
                                    readOnly={isVerified}
                                    rightIcon={isVerified ? <GreenThick /> : undefined}
                                />
                            )}
                        />

                        <Controller
                            name="last_name"
                            control={control}
                            rules={{ required: 'Last name is required' }}
                            render={({ field, fieldState }) => (
                                <Input
                                    label="Last Name"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState?.error?.message}
                                    className="w-full"
                                    readOnly={isVerified}
                                    rightIcon={isVerified ? <GreenThick /> : undefined}
                                />
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="bvn"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    label="Bank Verification Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="w-full"
                                    icon={<BVNIcon />}
                                    readOnly={isVerified}
                                    rightIcon={isVerified ? <GreenThick /> : undefined}
                                />
                            )}
                        />

                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    label="WhatsApp Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="w-full"
                                    icon={<WhatsappIcon />}
                                    readOnly={isVerified}
                                    rightIcon={isVerified ? <GreenThick /> : undefined}
                                />
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="Gender"
                                    options={genderOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="w-full"
                                />
                            )}
                        />

                        <Controller
                            name="date_of_birth"
                            control={control}
                            render={({ field }) => (
                                <DateOfBirthPicker
                                    label="Date of Birth"
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="w-full"
                                    readOnly={isVerified}
                                    rightIcon={isVerified ? <GreenThick /> : undefined}
                                />
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Invalid email address',
                                },
                            }}
                            render={({ field, fieldState }) => (
                                <Input
                                    type="email"
                                    label="Email Address"
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={fieldState?.error?.message}
                                    className="w-full"
                                    icon={<MailIcon />}
                                />
                            )}
                        />

                        <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label="State of Residence"
                                    options={stateOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="w-full"
                                />
                            )}
                        />
                    </div>

                    {isVerified && (
                        <div className="flex gap-2 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
                                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#00A86B" />
                            </svg>
                            <p className="text-sm text-gray-600">
                                Some fields are locked because they were verified using your BVN. If something doesn't look right, our Support team is here to help.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <Button type="button" variant="default" size="md" onClick={onClose} disabled={isSubmitting}>
                            Discard Changes
                        </Button>
                        <Button type="submit" variant="primary" size="md" disabled={isSubmitting}>
                            {isSubmitting ? 'Updating...' : 'Update Profile'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProfileModal;
